import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Filter, Search, ArrowRight, Clock, CheckCircle } from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { api } from '../lib/auth';
import { formatDate, getStatusColor, getStatusLabel, cn } from '../lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const STATUS_FILTERS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'needs_client_approval', label: 'Needs Approval' },
  { value: 'completed', label: 'Completed' },
];

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadOrders();
  }, [statusFilter]);

  const loadOrders = async () => {
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      const response = await api.getOrders(params);
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to load orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      order.service_title?.toLowerCase().includes(searchLower) ||
      order.department_name?.toLowerCase().includes(searchLower) ||
      order.summary?.toLowerCase().includes(searchLower)
    );
  });

  const activeOrders = filteredOrders.filter(o => !['completed', 'cancelled'].includes(o.status));
  const completedOrders = filteredOrders.filter(o => ['completed', 'cancelled'].includes(o.status));

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8"
        >
          <div>
            <h1 className="font-heading text-3xl font-bold text-white mb-2">Your Orders</h1>
            <p className="text-gray-400">Track and manage all your missions</p>
          </div>
          <Link to="/marketplace">
            <Button className="btn-primary" data-testid="new-order-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Order
            </Button>
          </Link>
        </motion.div>

        {/* Filters */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass rounded-xl p-4 mb-8"
        >
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <Input
                type="text"
                placeholder="Search orders..."
                className="pl-10 input-dark"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="orders-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48 input-dark" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent className="bg-card border-white/10">
                {STATUS_FILTERS.map((status) => (
                  <SelectItem key={status.value} value={status.value}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </motion.div>

        {/* Active Orders */}
        {activeOrders.length > 0 && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mb-12"
          >
            <h2 className="font-heading text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-yellow-400" />
              Active Orders ({activeOrders.length})
            </h2>
            <div className="space-y-4">
              {activeOrders.map((order, i) => (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Link to={`/orders/${order.id}`} data-testid={`order-${order.id}`}>
                    <div className="glass rounded-xl p-6 hover:border-primary/30 transition-colors">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <span className={cn(
                              "px-3 py-1 rounded-full text-xs font-medium border",
                              getStatusColor(order.status)
                            )}>
                              {getStatusLabel(order.status)}
                            </span>
                            <span className="text-sm text-gray-500">
                              Ordered: {formatDate(order.created_at)}
                            </span>
                            {(order.structured_data_json?.desired_due_date || order.desired_due_date) && (
                              <span className="text-sm text-orange-400">
                                • Due: {formatDate(order.structured_data_json?.desired_due_date || order.desired_due_date)}
                              </span>
                            )}
                          </div>
                          <h3 className="font-semibold text-white text-lg mb-1">
                            {order.structured_data_json?.task_name || order.service_title || 'Order'}
                            {order.structured_data_json?.client_name && (
                              <span className="text-gray-400 font-normal text-base ml-2">
                                — {order.structured_data_json.client_name}
                              </span>
                            )}
                          </h3>
                          <p className="text-sm text-gray-400">
                            {order.department_name}
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          {order.status === 'needs_client_approval' && (
                            <span className="px-3 py-1 rounded-full bg-accent/20 text-accent text-xs font-medium animate-pulse">
                              Action Required
                            </span>
                          )}
                          <ArrowRight className="w-5 h-5 text-gray-500" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Completed Orders */}
        {completedOrders.length > 0 && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <h2 className="font-heading text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              Completed ({completedOrders.length})
            </h2>
            <div className="space-y-4">
              {completedOrders.map((order, i) => (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Link to={`/orders/${order.id}`}>
                    <div className="glass rounded-xl p-4 hover:border-primary/30 transition-colors opacity-75 hover:opacity-100">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-medium text-white mb-1">
                            {order.structured_data_json?.task_name || order.service_title || 'Order'}
                            {order.structured_data_json?.client_name && (
                              <span className="text-gray-400 font-normal text-sm ml-2">
                                — {order.structured_data_json.client_name}
                              </span>
                            )}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {order.department_name} • Ordered: {formatDate(order.created_at)}
                            {(order.structured_data_json?.desired_due_date || order.desired_due_date) && (
                              <span className="text-orange-400">
                                {' '}• Due: {formatDate(order.structured_data_json?.desired_due_date || order.desired_due_date)}
                              </span>
                            )}
                          </p>
                        </div>
                        <span className={cn(
                          "px-3 py-1 rounded-full text-xs font-medium border",
                          getStatusColor(order.status)
                        )}>
                          {getStatusLabel(order.status)}
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Empty State */}
        {!loading && filteredOrders.length === 0 && (
          <div className="text-center py-16">
            <Clock className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 mb-4">No orders found</p>
            <Link to="/marketplace">
              <Button className="btn-primary">Start Your First Mission</Button>
            </Link>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-white/5 rounded-xl animate-pulse" />
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
